import { cn } from "@/lib/utils";
import { DateRangeSelector, type DateRangeValue } from "@/components/ui/date-range-selector";

export type PortfolioTab = "overview" | "transfers" | "trades" | "funding";

type PortfolioHeaderCardProps = {
  activeTab: PortfolioTab;
  onTabChange: (tab: PortfolioTab) => void;
  dateRange: DateRangeValue;
  onDateRangeChange: (range: DateRangeValue) => void;
  isSticky?: boolean;
};

const tabs: { value: PortfolioTab; label: string }[] = [
  { value: "overview", label: "Overview" },
  { value: "transfers", label: "Transfers" },
  { value: "trades", label: "History" },
  { value: "funding", label: "Funding" },
];

export const PortfolioHeaderCard = ({
  activeTab,
  onTabChange,
  dateRange,
  onDateRangeChange,
  isSticky = false,
}: PortfolioHeaderCardProps) => {
  return (
    <div
      data-portfolio-header
      className={cn(
        "rounded-lg bg-card border border-border/50 shadow-sm dark:shadow-none transition-all duration-300",
        isSticky
          ? "sticky top-0 z-50 py-2 px-3 md:px-5 backdrop-blur-sm bg-card/95 shadow-md"
          : "p-3 md:px-5 md:py-4"
      )}
    >
      {/* Mobile Layout */}
      <div className="lg:hidden space-y-4">
        {/* Title */}
        <div className="transition-all duration-300">
          <h1 className="text-xl font-bold text-foreground">Account Overview</h1>
          <p
            className={cn(
              "text-muted-foreground text-sm transition-all duration-300 overflow-hidden",
              isSticky ? "max-h-0 opacity-0 mt-0" : "max-h-10 opacity-100 mt-0.5"
            )}
          >
            Account health, history, and settings
          </p>
        </div>

        {/* Tabs + Time Interval */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full">
          {/* Tabs - horizontally scrollable on mobile */}
          <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-lg overflow-x-auto scrollbar-none max-w-full">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => onTabChange(tab.value)}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-all whitespace-nowrap shrink-0",
                  activeTab === tab.value
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Date Range Selector */}
          <DateRangeSelector
            value={dateRange}
            onChange={onDateRangeChange}
          />
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:flex items-center justify-between">
        {/* Title */}
        <div>
          <h1 className="text-xl font-bold text-foreground">Account Overview</h1>
          <p
            className={cn(
              "text-muted-foreground text-sm transition-all duration-300 overflow-hidden",
              isSticky ? "max-h-0 opacity-0 mt-0" : "max-h-10 opacity-100 mt-0.5"
            )}
          >
            Account health, history, and settings
          </p>
        </div>

        {/* Tabs (centered) */}
        <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-lg">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => onTabChange(tab.value)}
              className={cn(
                "px-4 py-1.5 text-sm font-medium rounded-md transition-all",
                activeTab === tab.value
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Date Range */}
        <DateRangeSelector
          value={dateRange}
          onChange={onDateRangeChange}
        />
      </div>
    </div>
  );
};
